import { PageContainer } from '../../component/page-container/page-container'
import { Button } from '../../component/ui/button'
import { Input } from '../../component/ui/input/input'
import { PageTitle } from '../../component/ui/page-title'
import { imageUtil } from '../../common/util/image-util'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { GroupType, GroupVisibility, type UpdateGroupPayload } from '../../types/groups'
import {
  useAddAdminGroupMembers,
  useAdminGroup,
  useAdminGroupMembers,
  useAdminGroupPosts,
  useDeleteAdminGroup,
  useRemoveAdminGroupMember,
  useUpdateAdminGroup,
} from '../../hook/use-groups'
import { isFeatureFlagged } from 'src/util/feature-flags'
import { stringUtil } from 'src/util/string-util'
import { toasterUtil } from 'src/util/toaster-util'

export const AdminGroupDetailScreen = (): React.ReactElement => {
  const { groupId } = useParams<{ groupId: string }>()
  const navigate = useNavigate()
  const [isEditing, setIsEditing] = useState(false)
  const [membersOffset] = useState(0)
  const [postsOffset] = useState(0)
  const [newMemberIds, setNewMemberIds] = useState('')
  const limit = 20

  const { data: group, isLoading: isLoadingGroup, error: groupError } = useAdminGroup(groupId!)
  const {
    data: membersData,
    isLoading: isLoadingMembers,
    refetch: refetchMembers,
  } = useAdminGroupMembers(groupId!, { limit, offset: membersOffset })
  const { data: postsData, isLoading: isLoadingPosts } = useAdminGroupPosts(groupId!, { limit, offset: postsOffset })

  const [editFormData, setEditFormData] = useState<UpdateGroupPayload>({})

  React.useEffect(() => {
    if (group && isEditing) {
      setEditFormData({
        name: group.name,
        description: group.description,
        type: group.type,
        visibility: group.visibility,
        isActive: group.isActive,
      })
    }
  }, [group, isEditing])

  const updateMutation = useUpdateAdminGroup()
  const deleteMutation = useDeleteAdminGroup()
  const removeMemberMutation = useRemoveAdminGroupMember()
  const addMembersMutation = useAddAdminGroupMembers()

  useEffect(() => {
    if (isFeatureFlagged('GROUPS')) {
      navigate('/', { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    if (groupError && (groupError as any)?.response?.status === 403) {
      navigate('/', { replace: true })
    }
  }, [groupError, navigate])

  const handleUpdate = (): void => {
    if (!groupId) return
    updateMutation.mutate(
      { groupId, input: editFormData },
      {
        onSuccess: () => {
          toasterUtil.success('Group updated successfully')
          setIsEditing(false)
        },
        onError: () => {
          toasterUtil.error('Failed to update group')
        },
      },
    )
  }

  const handleDelete = (): void => {
    if (!groupId) return
    if (window.confirm(`Are you sure you want to delete "${group?.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(groupId, {
        onSuccess: () => {
          toasterUtil.success('Group deleted successfully')
          navigate('/admin/groups')
        },
        onError: () => {
          toasterUtil.error('Failed to delete group')
        },
      })
    }
  }

  const handleRemoveMember = (memberId: string, memberName: string): void => {
    if (!groupId) return
    if (window.confirm(`Remove ${memberName} from this group?`)) {
      removeMemberMutation.mutate(
        { groupId, memberId },
        {
          onSuccess: () => {
            toasterUtil.success('Member removed successfully')
            refetchMembers()
          },
          onError: () => {
            toasterUtil.error('Failed to remove member')
          },
        },
      )
    }
  }

  const handleAddMembers = (): void => {
    if (!groupId) return
    const ids = newMemberIds
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0)
    if (ids.length === 0) {
      toasterUtil.error('Please enter at least one hustler ID')
      return
    }
    addMembersMutation.mutate(
      { groupId, hustlerIds: ids },
      {
        onSuccess: () => {
          toasterUtil.success('Members added successfully')
          refetchMembers()
          setNewMemberIds('')
        },
        onError: () => {
          toasterUtil.error('Failed to add members')
        },
      },
    )
  }

  if (isLoadingGroup) {
    return (
      <PageContainer>
        <div className="py-6 md:py-[82px]">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded w-1/3 mb-6"></div>
            <div className="h-32 bg-gray-700 rounded"></div>
          </div>
        </div>
      </PageContainer>
    )
  }

  if (!group) {
    return (
      <PageContainer>
        <div className="py-6 md:py-[82px]">
          <PageTitle>Group Not Found</PageTitle>
        </div>
      </PageContainer>
    )
  }

  const members = membersData?.data ?? []
  const posts = postsData?.data ?? []
  const membersTotal = membersData?.total ?? 0
  const postsTotal = postsData?.total ?? 0

  return (
    <PageContainer>
      <div className="py-6 md:py-[82px]">
        <div className="mb-6 flex items-center justify-between">
          <PageTitle>{`Admin - ${group.name}`}</PageTitle>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => navigate('/admin/groups')}>
              Back to List
            </Button>
            {!isEditing && <Button onClick={() => setIsEditing(true)}>Edit Group</Button>}
            <Button variant="secondary" onClick={handleDelete}>
              Delete Group
            </Button>
          </div>
        </div>

        {/* Group Details */}
        <div className="bg-dark-gray rounded-lg p-6 mb-6">
          <h3 className="text-ivory font-evogria text-xl mb-4">Group Details</h3>

          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name</label>
                <Input
                  value={editFormData.name ?? ''}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={editFormData.description ?? ''}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="w-full bg-secondary-gray text-light-gray rounded-md px-4 py-2 text-sm placeholder:text-mushroom focus:outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Type</label>
                <select
                  value={editFormData.type ?? group.type}
                  onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value as GroupType })}
                  className="w-full bg-gray-800 text-ivory border border-gray-700 rounded px-3 py-2"
                >
                  <option value={GroupType.GENERAL}>General</option>
                  <option value={GroupType.CHAMPIONS}>Champions</option>
                  <option value={GroupType.COMPANY}>Company</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Visibility</label>
                <select
                  value={editFormData.visibility ?? group.visibility}
                  onChange={(e) => setEditFormData({ ...editFormData, visibility: e.target.value as GroupVisibility })}
                  className="w-full bg-gray-800 text-ivory border border-gray-700 rounded px-3 py-2"
                >
                  <option value={GroupVisibility.PRIVATE}>Private</option>
                  <option value={GroupVisibility.PUBLIC}>Public</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Status</label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editFormData.isActive ?? group.isActive}
                    onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })}
                  />
                  <span className="text-ivory">Active</span>
                </label>
              </div>

              <div className="flex gap-3">
                <Button onClick={handleUpdate} disabled={updateMutation.isLoading}>
                  {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="secondary" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-gray-400">Description:</span>
                <p className="text-ivory mt-1">{group.description || 'No description'}</p>
              </div>
              <div>
                <span className="text-gray-400">Type:</span>
                <span className="text-ivory ml-2 capitalize">{group.type}</span>
              </div>
              <div>
                <span className="text-gray-400">Visibility:</span>
                <span className="text-ivory ml-2 capitalize">{group.visibility}</span>
              </div>
              <div>
                <span className="text-gray-400">Status:</span>
                <span className={`ml-2 ${group.isActive ? 'text-green-400' : 'text-red-400'}`}>
                  {group.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Invite Code:</span>
                <code className="ml-2 text-ivory bg-gray-800 px-2 py-1 rounded">{group.inviteCode || 'None'}</code>
              </div>
              <div>
                <span className="text-gray-400">Created:</span>
                <span className="text-ivory ml-2">{new Date(group.createdAt).toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>

        {/* Members Section */}
        <div className="bg-dark-gray rounded-lg p-6 mb-6">
          <h3 className="text-ivory font-evogria text-xl mb-4">Members ({membersTotal})</h3>

          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-1">Add Members</label>
            <div className="flex gap-2">
              <Input
                value={newMemberIds}
                onChange={(e) => setNewMemberIds(e.target.value)}
                placeholder="Enter hustler IDs (comma-separated)"
              />
              <Button onClick={handleAddMembers} disabled={!newMemberIds.trim() || addMembersMutation.isLoading}>
                {addMembersMutation.isLoading ? 'Adding...' : 'Add'}
              </Button>
            </div>
          </div>

          {isLoadingMembers ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-700 rounded animate-pulse"></div>
              ))}
            </div>
          ) : members.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No members yet</p>
          ) : (
            <div className="space-y-3">
              {members.map((member) => {
                const memberImage = member.hustler?.profile?.profilePhotoFileId
                  ? imageUtil.getImageUrl(member.hustler.profile.profilePhotoFileId)
                  : undefined
                const displayName = stringUtil.getDisplayName(member.hustler ?? null)

                return (
                  <div key={member.id} className="flex items-center justify-between bg-gray-800 rounded p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-700 overflow-hidden flex-shrink-0">
                        {memberImage ? (
                          <img src={memberImage} alt={displayName} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-ivory text-sm">
                            {displayName.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-ivory font-semibold">{displayName}</p>
                        <p className="text-gray-400 text-xs capitalize">{member.role}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleRemoveMember(member.id, displayName)}
                      disabled={removeMemberMutation.isLoading}
                    >
                      Remove
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Posts Section */}
        <div className="bg-dark-gray rounded-lg p-6">
          <h3 className="text-ivory font-evogria text-xl mb-4">Posts ({postsTotal})</h3>

          {isLoadingPosts ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-gray-700 rounded animate-pulse"></div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No posts yet</p>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <div key={post.id} className="bg-gray-800 rounded p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-ivory font-semibold">{stringUtil.getDisplayName(post.hustler ?? null)}</p>
                      <p className="text-gray-400 text-xs">{new Date(post.createdAt).toLocaleString()}</p>
                    </div>
                    {post.isPinned && <span className="text-yellow-500 text-xs font-semibold">PINNED</span>}
                  </div>
                  <p className="text-gray-300 whitespace-pre-wrap">{post.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  )
}
